/* Simulated device controls; no media permissions or operating system actions. */
(() => {
  const tag = 'muteme-regular-demo';
  if (customElements.get(tag)) return;
  const devices = {
    original: { name: 'MuteMe Original', action: 'Touch', surface: 'steel surface', label: 'Stainless steel. One touch.' },
    mini: { name: 'MuteMe Mini', action: 'Touch', surface: 'Mini touch surface', label: 'Small footprint. The same touch.' },
    mutesync: { name: 'MuteSync', action: 'Press', surface: 'physical button', label: 'A physical press. A visible response.' }
  };
  const discoverDevices = { '#discover-original': 'original', '#discover-mini': 'mini', '#discover-mutesync': 'mutesync' };
  customElements.define(tag, class extends HTMLElement {
    get deviceProfile() { return devices[this.selectedDevice] || devices.original; }
    get modeCopy() {
      const { action, surface } = this.deviceProfile;
      return {
        press: ['Toggle on press', action + ' to mute. ' + action + ' again to unmute.', 'The microphone toggles as soon as you ' + action.toLowerCase() + ' the ' + surface + '.'],
        release: ['Toggle on release', action + ', then release to toggle.', 'The microphone stays as it is while you hold, then toggles when you release.'],
        talk: ['Push to talk', action + ' and hold to talk.', 'Muted by default. Hold to speak; release to mute again.'],
        mute: ['Push to mute', action + ' and hold to mute.', 'Live by default. Hold to mute; release to speak again.']
      };
    }
    connectedCallback() {
      this.initFrame = requestAnimationFrame(() => this.initialize());
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.initFrame);
      this.finishTouch(true);
      if (this.events) this.events.abort();
      this.initialized = false;
    }
    initialize() {
      if (!this.isConnected || this.initialized) return;
      if (!this.querySelector('[data-demo-end]')) {
        this.initFrame = requestAnimationFrame(() => this.initialize()); return;
      }
      this.initialized = true;
      this.puck = this.querySelector('[data-device]');
      this.stage = this.querySelector('.mm-stage');
      this.feedback = this.querySelector('[data-feedback]');
      this.defaultDevice = Object.prototype.hasOwnProperty.call(devices, this.dataset.defaultDevice) ? this.dataset.defaultDevice : 'original';
      // Liquid resolves the product/section default. Reattached instances retain
      // their current selection; invalid or missing state falls back to that default.
      this.selectedDevice = Object.prototype.hasOwnProperty.call(devices, this.dataset.selectedDevice) ? this.dataset.selectedDevice : this.defaultDevice;
      this.palette = { '#c10000': 'Red', '#4caf50': 'Green', '#2196f3': 'Blue', '#ffeb00': 'Yellow', '#00bcd4': 'Cyan', '#b45af5': 'Purple', '#f0f0f0': 'White', '#000000': 'Off' };
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.addEventListener('click', event => this.onClick(event), options);
      this.puck.addEventListener('pointerdown', event => {
        if (event.button !== 0 || !event.isPrimary || this.activeInput) return;
        event.preventDefault();
        this.puck.focus({ preventScroll: true });
        this.activeInput = { type: 'pointer', id: event.pointerId };
        try { this.puck.setPointerCapture(event.pointerId); } catch (_) {}
        this.beginTouch();
      }, options);
      window.addEventListener('pointerup', event => {
        if (this.activeInput?.type === 'pointer' && this.activeInput.id === event.pointerId) this.finishTouch(false);
      }, options);
      window.addEventListener('pointercancel', event => {
        if (this.activeInput?.type === 'pointer' && this.activeInput.id === event.pointerId) this.finishTouch(true);
      }, options);
      this.puck.addEventListener('lostpointercapture', event => {
        if (this.activeInput?.type === 'pointer' && this.activeInput.id === event.pointerId) this.finishTouch(true);
      }, options);
      this.puck.addEventListener('keydown', event => {
        if (![' ', 'Enter'].includes(event.key)) return;
        event.preventDefault();
        if (event.repeat || this.activeInput) return;
        this.activeInput = { type: 'keyboard', key: event.key };
        this.beginTouch();
      }, options);
      window.addEventListener('keyup', event => {
        if (this.activeInput?.type === 'keyboard' && this.activeInput.key === event.key) {
          event.preventDefault(); this.finishTouch(false);
        }
      }, options);
      this.puck.addEventListener('blur', () => this.finishTouch(true), options);
      this.puck.addEventListener('contextmenu', event => event.preventDefault(), options);
      window.addEventListener('blur', () => this.finishTouch(true), options);
      window.addEventListener('pagehide', () => this.finishTouch(true), options);
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.finishTouch(true); }, options);
      this.querySelector('fieldset').disabled = false;
      this.reset();
      const discovery = this.closest('.mm-discovery');
      if (discovery) {
        const selectFromHash = () => {
          const device = discoverDevices[window.location.hash];
          if (device) this.selectDevice(device);
        };
        window.addEventListener('hashchange', selectFromHash, options);
        discovery.addEventListener('click', event => {
          if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          const link = event.target.closest('a[href^="#discover-"]');
          const device = link && discoverDevices[link.getAttribute('href')];
          if (device) this.selectDevice(device);
        }, options);
        selectFromHash();
      }
    }
    reset() {
      this.finishTouch(true);
      this.querySelector('.mm-colors').open = false;
      this.meeting = true;
      this.muted = true;
      this.mode = 'press';
      this.held = false;
      this.settings = {
        meeting: { muted: { color: '#c10000', effect: 'bright' }, live: { color: '#4caf50', effect: 'bright' } },
        idle: { muted: { color: '#f0f0f0', effect: 'dim' }, live: { color: '#f0f0f0', effect: 'dim' } }
      };
      this.render();
      this.renderColors();
      this.say('You’re muted. ' + this.deviceProfile.action + ' the ' + this.deviceProfile.surface + ' to join the conversation.');
    }
    selectDevice(device) {
      if (!Object.prototype.hasOwnProperty.call(devices, device) || device === this.selectedDevice) return;
      // Finish a held gesture against the old device before swapping its artwork.
      // A later pointer/key release must not change the newly selected device.
      this.finishTouch(true);
      this.selectedDevice = device;
      this.render();
      this.say(this.deviceProfile.name + ' selected. Microphone ' + (this.muted ? 'muted.' : 'live.') + ' Your mode and colors stay the same.');
    }
    say(message) { this.feedback.textContent = message; }
    beginTouch() {
      this.held = true;
      if (this.mode === 'press') this.muted = !this.muted;
      if (this.mode === 'talk') this.muted = false;
      if (this.mode === 'mute') this.muted = true;
      this.render();
      this.say(this.mode === 'release' ? 'Holding. Release to toggle your microphone.' : this.mode === 'talk' ? 'You’re live while you hold. Release to mute.' : this.mode === 'mute' ? 'You’re muted while you hold. Release to speak.' : ('Microphone ' + (this.muted ? 'muted' : 'unmuted') + ' on ' + this.deviceProfile.action.toLowerCase() + '.'));
    }
    finishTouch(cancelled) {
      if (!this.activeInput) return;
      const input = this.activeInput;
      this.activeInput = null;
      this.held = false;
      if (this.mode === 'release' && !cancelled) this.muted = !this.muted;
      if (this.mode === 'talk') this.muted = true;
      if (this.mode === 'mute') this.muted = false;
      if (input.type === 'pointer' && this.puck.hasPointerCapture(input.id)) this.puck.releasePointerCapture(input.id);
      this.render();
      this.say(cancelled ? this.deviceProfile.action + ' ended. ' + (this.muted ? 'Microphone muted.' : 'Microphone live.') : this.mode === 'release' ? (this.muted ? 'Muted on release.' : 'Unmuted on release.') : this.mode === 'talk' ? 'Released. Back to muted.' : this.mode === 'mute' ? 'Released. Your microphone is live again.' : (this.muted ? 'You’re muted. ' + this.deviceProfile.action + ' again to speak.' : 'You’re live. ' + this.deviceProfile.action + ' again to mute.'));
    }
    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || button.disabled) return;
      if (button.hasAttribute('data-device')) {
        // Native mouse/touch and keyboard actions are handled on down/up.
        // A zero-detail click without a pointerType is an assistive activation.
        if (event.detail === 0 && !event.pointerType && !this.activeInput) {
          this.activeInput = { type: 'assistive' };
          this.beginTouch(); this.finishTouch(false);
        }
        return;
      }
      if (button.dataset.demoDevice) { this.selectDevice(button.dataset.demoDevice); return; }
      this.finishTouch(true);
      if (button.dataset.mode) {
        this.mode = button.dataset.mode;
        if (this.mode === 'talk') this.muted = true;
        if (this.mode === 'mute') this.muted = false;
        this.render(); this.say(this.modeCopy[this.mode][2]); return;
      }
      if (button.dataset.environment) {
        this.meeting = button.dataset.environment === 'meeting';
        this.render();
        this.say(this.meeting ? 'In a meeting. Your meeting colors are active.' : 'Between meetings. Your idle colors are active; the device still controls your microphone.'); return;
      }
      if (button.dataset.action === 'reset') { this.reset(); return; }
      if (button.dataset.action === 'mic') {
        this.muted = !this.muted; this.render();
        this.say((this.meeting ? 'Demo call' : 'System microphone') + (this.muted ? ' muted.' : ' unmuted.') + ' The device lighting follows.'); return;
      }
      const group = button.closest('[data-color-context]');
      const row = button.closest('[data-color-state]');
      if (!group || !row) return;
      const setting = this.settings[group.dataset.colorContext][row.dataset.colorState];
      if (button.dataset.color && Object.prototype.hasOwnProperty.call(this.palette, button.dataset.color)) {
        setting.color = button.dataset.color;
      } else if (button.dataset.effect && setting.color !== '#000000' && ['bright', 'dim', 'pulse'].includes(button.dataset.effect)) {
        setting.effect = button.dataset.effect;
      } else return;
      this.renderColors(); this.render();
      const context = group.dataset.colorContext === 'meeting' ? 'In a meeting' : 'Not in a meeting';
      this.say(context + ', ' + (row.dataset.colorState === 'muted' ? 'muted' : 'unmuted') + ': ' + this.palette[setting.color] + (setting.color === '#000000' ? '. Lighting effects are off.' : ', ' + setting.effect + '.'));
    }
    renderColors() {
      this.querySelectorAll('[data-color-context]').forEach(group => {
        group.querySelectorAll('[data-color-state]').forEach(row => {
          const setting = this.settings[group.dataset.colorContext][row.dataset.colorState];
          row.querySelector('[data-color-name]').textContent = this.palette[setting.color];
          row.querySelectorAll('[data-color]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.color === setting.color)));
          row.querySelectorAll('[data-effect]').forEach(button => {
            button.disabled = setting.color === '#000000';
            button.setAttribute('aria-pressed', String(!button.disabled && button.dataset.effect === setting.effect));
          });
        });
      });
    }
    render() {
      const { name, action, surface, label } = this.deviceProfile;
      this.dataset.selectedDevice = this.selectedDevice;
      this.querySelectorAll('[data-demo-device]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.demoDevice === this.selectedDevice)));
      this.querySelectorAll('[data-device-details]').forEach(panel => panel.hidden = panel.dataset.deviceDetails !== this.selectedDevice);
      this.querySelector('[data-device-label]').textContent = label;
      this.querySelector('[data-device-instruction]').textContent = 'Mouse, touch, or focus the device and hold Space or Enter.';
      this.querySelector('[data-press-description]').textContent = 'Switch as soon as you ' + action.toLowerCase();
      this.querySelector('[data-mode="release"] > span').textContent = 'Switch when you let go';
      this.querySelector('.mm-mode-list').setAttribute('aria-label', name + ' microphone mode');
      const state = this.muted ? 'muted' : 'live';
      const context = this.meeting ? 'meeting' : 'idle';
      const setting = this.settings[context][state];
      const level = setting.color === '#000000' ? 0 : setting.effect === 'dim' ? .28 : 1;
      const rgb = [1, 3, 5].map(i => parseInt(setting.color.slice(i, i + 2), 16));
      const peak = Math.max(...rgb);
      const light = peak ? '#' + rgb.map(v => Math.round(v * 255 / peak).toString(16).padStart(2, '0')).join('') : '#000000';
      this.stage.style.setProperty('--mm-light', light);
      this.stage.style.setProperty('--mm-level', String(level));
      this.stage.dataset.effect = level ? setting.effect : 'off';
      this.dataset.microphone = state;
      this.puck.dataset.held = String(this.held);
      this.puck.setAttribute('aria-pressed', String(this.held));
      this.puck.setAttribute('aria-label', name + ' ' + surface + '. ' + this.modeCopy[this.mode][0] + '. Microphone ' + (this.muted ? 'muted.' : 'live.'));
      this.querySelectorAll('[data-mode]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.mode === this.mode)));
      this.querySelectorAll('[data-environment]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.environment === context)));
      this.querySelectorAll('[data-color-context]').forEach(group => group.dataset.active = String(group.dataset.colorContext === context));
      this.querySelector('[data-mode-explanation]').textContent = this.modeCopy[this.mode][2];
      this.querySelector('[data-touch-hint]').textContent = this.held ? (this.mode === 'release' ? 'Release to toggle your microphone.' : action === 'Press' ? 'Button held.' : 'Touch detected.') : this.mode === 'press' ? (action + (this.muted ? ' to unmute.' : ' to mute.')) : this.mode === 'release' ? (action + ', then release to ' + (this.muted ? 'unmute.' : 'mute.')) : this.modeCopy[this.mode][1];
      this.querySelector('[data-device-status]').textContent = this.muted ? 'Microphone muted' : 'Microphone live';
      this.querySelector('[data-screen-title]').textContent = this.meeting ? 'The team catch-up' : 'A moment between meetings';
      this.querySelector('[data-screen-meta]').textContent = this.meeting ? '4 people · Demo call' : 'No active meeting';
      this.querySelector('.mm-tiles').hidden = !this.meeting;
      this.querySelector('.mm-idle').hidden = this.meeting;
      this.querySelector('.mm-you').dataset.live = String(!this.muted);
      this.querySelectorAll('[data-mic-slash]').forEach(path => path.hidden = !this.muted);
      // SVG elements do not consistently implement the HTML hidden property.
      this.querySelectorAll('[data-mic-slash]').forEach(path => path.style.display = this.muted ? '' : 'none');
      this.querySelector('[data-action="mic"]').setAttribute('aria-pressed', String(this.muted));
      this.querySelector('[data-action="mic"]').setAttribute('aria-label', (this.muted ? 'Unmute' : 'Mute') + ' the simulated microphone');
      this.querySelector('[data-call-state]').textContent = this.muted ? 'Your microphone is muted' : 'Your microphone is live';
      this.querySelector('[data-call-detail]').textContent = this.meeting ? (this.muted ? 'The conversation can wait.' : 'You have the floor.') : 'System microphone · No active meeting';
      this.querySelector('.mm-wave').dataset.live = String(!this.muted);
    }
  });
})();
